import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { 
  HelpCircle, X, ChevronRight, FileText, Video, Users, 
  TrendingUp, Settings, Send, CreditCard, Building2, User,
  Lightbulb, CheckCircle
} from 'lucide-react';

// Page-specific help content - helpful without being a blueprint
const helpContent = {
  '/': {
    title: 'Welcome to BrandStage',
    icon: <Building2 className="w-5 h-5" />,
    tips: [
      { q: 'What is BrandStage?', a: 'A video-first platform to build your company\'s digital presence through authentic leadership narratives and professional profiles.' },
      { q: 'How do I get started?', a: 'Create a free company profile, add your team\'s videos, and measure your digital readiness score.' },
      { q: 'What is BrandStage Connect?', a: 'A network where verified profiles can discover and respond to business opportunities.' }
    ]
  },
  '/profile/create': {
    title: 'Creating Your Profile',
    icon: <FileText className="w-5 h-5" />,
    tips: [
      { q: 'What makes a compelling profile?', a: 'Focus on your unique story, leadership voice, and what sets you apart. Authentic content builds trust.' },
      { q: 'Why add videos?', a: 'Video profiles get significantly more engagement. Even a simple founder message makes a difference.' },
      { q: 'What is the Readiness Score?', a: 'A benchmark showing how complete and engaging your digital presence is compared to industry standards.' },
      { q: 'Can I edit later?', a: 'Yes, you can update your profile anytime from your dashboard.' }
    ]
  },
  '/booth': {
    title: 'Quick Bite Recording',
    icon: <Video className="w-5 h-5" />,
    tips: [
      { q: 'What is a Quick Bite?', a: 'A 30-40 second video clip perfect for social media, testimonials, or product highlights.' },
      { q: 'How long do I have?', a: 'You have up to 40 seconds to record. Keep it concise and impactful.' },
      { q: 'Where does my video go?', a: 'Videos are processed and delivered to your email within seconds, ready for sharing.' },
      { q: 'Tips for a great recording?', a: 'Good lighting, look at the camera, speak clearly, and smile. Authenticity wins over perfection.' }
    ]
  },
  '/connect': {
    title: 'BrandStage Connect (Coming Soon)',
    icon: <Users className="w-5 h-5" />,
    tips: [
      { q: 'What is Connect?', a: 'A curated board where verified members will share and discover business opportunities. Coming soon.' },
      { q: 'When will it launch?', a: 'BrandStage Connect is coming soon — launching once we reach 50+ active profiles on the platform.' },
      { q: 'How can I prepare?', a: 'Complete your BrandStage profile and subscription. You\'ll be notified when Connect goes live.' },
      { q: 'Can I register interest?', a: 'Email operations@brandstage-media.com to express early interest.' }
    ]
  },
  '/dashboard': {
    title: 'Your Dashboard',
    icon: <Settings className="w-5 h-5" />,
    tips: [
      { q: 'What can I do here?', a: 'Manage your profiles, view recordings, track analytics, and handle payments.' },
      { q: 'How do I make my profile visible?', a: 'Activate a subscription to make your profile publicly discoverable.' },
      { q: 'Pending payments?', a: 'View and pay any invoices or subscription requests from the payments section.' }
    ]
  },
  '/admin': {
    title: 'Admin Dashboard',
    icon: <Settings className="w-5 h-5" />,
    isAdmin: true,
    tips: [
      { q: 'Creating a profile for a client?', a: 'Go to Profiles tab → Create new profile with client details. You can transfer ownership later.' },
      { q: 'How to transfer a profile?', a: 'Transfers tab → Initiate Transfer → Enter client email. They\'ll receive a claim link.' },
      { q: 'Sending a payment request?', a: 'Payments tab → New Payment Request → Set amount and currency. Client sees it in their dashboard.' },
      { q: 'Posting an opportunity?', a: 'Connect tab → Post Opportunity → Fill details and link to a profile if needed.' },
      { q: 'Managing subscriptions?', a: 'Use coupons for discounts or transfer profiles with subscription handling options.' }
    ]
  },
  '/claim-profile': {
    title: 'Claiming Your Profile',
    icon: <CheckCircle className="w-5 h-5" />,
    tips: [
      { q: 'What does claiming mean?', a: 'You become the owner of this profile and can edit, manage, and control its visibility.' },
      { q: 'Do I need to pay?', a: 'Depends on how the profile was set up. Some include active subscriptions, others may require payment.' },
      { q: 'Can I preview first?', a: 'Yes, click "Preview Profile" to see what\'s been created before you claim.' }
    ]
  }
};

// Default help for pages without specific content
const defaultHelp = {
  title: 'Need Help?',
  icon: <HelpCircle className="w-5 h-5" />,
  tips: [
    { q: 'How can I get support?', a: 'Contact us at support@brandstage-media.com or use the contact form on our website.' },
    { q: 'Where can I learn more?', a: 'Visit our homepage to explore all BrandStage features and services.' }
  ]
};

export default function ContextualHelp() {
  const [isOpen, setIsOpen] = useState(false);
  const [expandedIndex, setExpandedIndex] = useState(null);
  const location = useLocation();

  // Get help content for current page
  const getHelpContent = () => {
    // Check for exact match first
    if (helpContent[location.pathname]) {
      return helpContent[location.pathname];
    }
    
    // Check for partial matches (e.g., /admin/something matches /admin)
    for (const path of Object.keys(helpContent)) {
      if (location.pathname.startsWith(path) && path !== '/') {
        return helpContent[path];
      }
    }
    
    // Check for claim-profile with token
    if (location.pathname.startsWith('/claim-profile')) {
      return helpContent['/claim-profile'];
    }
    
    return defaultHelp;
  };

  const currentHelp = getHelpContent();

  // Close when navigating
  useEffect(() => {
    setIsOpen(false);
    setExpandedIndex(null);
  }, [location.pathname]);

  return (
    <>
      {/* Floating Help Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full shadow-lg transition-all duration-300 flex items-center justify-center ${
          isOpen 
            ? 'bg-zinc-700 hover:bg-zinc-600' 
            : 'bg-[#C41E3A] hover:bg-[#A01830]'
        }`}
        data-testid="contextual-help-btn"
        aria-label="Help"
      >
        {isOpen ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <HelpCircle className="w-6 h-6 text-white" />
        )}
      </button>

      {/* Help Panel */}
      {isOpen && (
        <div 
          className="fixed bottom-24 right-6 z-50 w-80 max-h-[70vh] bg-zinc-900 border border-zinc-700 rounded-2xl shadow-2xl overflow-hidden animate-slide-up"
          data-testid="contextual-help-panel"
        >
          {/* Header */}
          <div className="bg-gradient-to-r from-[#C41E3A] to-[#8B1428] p-4">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-xl bg-white/20 flex items-center justify-center text-white">
                {currentHelp.icon}
              </div>
              <div>
                <h3 className="font-bold text-white">{currentHelp.title}</h3>
                <p className="text-white/70 text-xs">Quick tips for this page</p>
              </div>
            </div>
          </div>

          {/* Tips List */}
          <div className="max-h-[50vh] overflow-y-auto">
            {currentHelp.tips.map((tip, index) => (
              <div key={index} className="border-b border-zinc-800 last:border-0">
                <button
                  onClick={() => setExpandedIndex(expandedIndex === index ? null : index)}
                  className="w-full p-4 text-left hover:bg-zinc-800/50 transition-colors flex items-start gap-3"
                >
                  <Lightbulb className={`w-4 h-4 mt-0.5 flex-shrink-0 transition-colors ${
                    expandedIndex === index ? 'text-[#C41E3A]' : 'text-zinc-500'
                  }`} />
                  <div className="flex-1">
                    <p className={`text-sm font-medium transition-colors ${
                      expandedIndex === index ? 'text-white' : 'text-zinc-300'
                    }`}>
                      {tip.q}
                    </p>
                    {expandedIndex === index && (
                      <p className="text-sm text-zinc-400 mt-2 leading-relaxed animate-fade-in">
                        {tip.a}
                      </p>
                    )}
                  </div>
                  <ChevronRight className={`w-4 h-4 text-zinc-500 transition-transform ${
                    expandedIndex === index ? 'rotate-90' : ''
                  }`} />
                </button>
              </div>
            ))}
          </div>

          {/* Footer */}
          <div className="p-4 bg-zinc-800/50 border-t border-zinc-700">
            <p className="text-xs text-zinc-500 text-center">
              Need more help?{' '}
              <a href="mailto:support@brandstage-media.com" className="text-[#C41E3A] hover:underline">
                Contact Support
              </a>
            </p>
          </div>
        </div>
      )}

      {/* Backdrop for mobile */}
      {isOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 md:hidden"
          onClick={() => setIsOpen(false)}
        />
      )}
    </>
  );
}
